// ======================================================================
// File: ConsolidateIndentDrawer.jsx
// Module: PPE → Procurement → Purchase Request
//
// Purpose:
// - Select approved indent lines
// - Allow partial quantity selection
// - Consolidate into Purchase Request
//
// UI Pattern:
// - Cloned from IndentItemDrawer.js
// - Aligned with PR UI standards
// ======================================================================

import React, { useEffect, useState } from "react";

// EXISTING IMPORT – NOT REMOVED AS PER INSTRUCTION
import indentService from "../../indent/services/indentService";

// NEW IMPORT ADDED – TO CALL NEW PR API
import purchaseRequestService from "../services/purchaseRequestService";

import QuantityInput from "../components/QuantityInput";

import "../pr-ui.css";

export default function ConsolidateIndentDrawer({
  open,
  departmentId,
  onClose,
  onConfirm,
}) {

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});

  // ==============================================================
  // LOAD APPROVED INDENT LINES
  // ==============================================================
  useEffect(() => {
    if (open) {
      loadIndentLines();
      setSelected({});
    }
  }, [open]);

  const loadIndentLines = async () => {
    try {
      setLoading(true);

      // ------------------------------------------------------------------
      // OLD CALL (KEPT AS COMMENT – NOT REMOVED)
      // Reuse existing indent API (approved only)
      //
      // const res = await indentService.getIndentLines({
      //   status: "APPROVED",
      // });
      // ------------------------------------------------------------------

      // NEW CALL – USING PURCHASE REQUEST API FOR CONSOLIDATION
      const res = await purchaseRequestService.getApprovedIndentLinesForPR({
        department_id: departmentId
      });

      setRows(res?.data?.data || []);

    } catch (err) {
      console.error("Failed to load indent lines", err);
      alert("Failed to load approved indent lines");
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // HANDLE SELECTION
  // ==============================================================
  const toggleRow = (row) => {
    setSelected((prev) => {
      if (prev[row.id]) {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      }

      return {
        ...prev,
        [row.id]: {
          indent_line_id: row.id,
          qty: row.pending_qty || row.approved_qty,
        },
      };
    });
  };

  const updateQty = (rowId, qty) => {
    setSelected((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        qty,
      },
    }));
  };

  // ==============================================================
  // CONFIRM CONSOLIDATION
  // ==============================================================
  const handleConfirm = () => {
    const payload = Object.values(selected);

    if (!payload.length) {
      alert("Please select at least one indent line");
      return;
    }

    onConfirm(payload);
  };

  // ==============================================================
  // RENDER
  // ==============================================================
  if (!open) return null;

  return (
    <div className="pr-drawer open nested">

      {/* HEADER */}
      <div className="pr-drawer-header">
        <h3>Select Approved Indent Lines</h3>

        <button
          className="btn-secondary"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="pr-drawer-body">

        {loading && (
          <div className="pr-empty-state">
            Loading approved indent lines...
          </div>
        )}

        {!loading && (
          <div className="pr-table-wrapper">
            <table className="pr-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Indent No</th>
                  <th>Material</th>
                  <th>Approved Qty</th>
                  <th>Pending Qty</th>
                  <th>Qty</th>
                  <th>UOM</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      No approved indent lines available
                    </td>
                  </tr>
                )}

                {rows.map((row) => {
                  const checked = !!selected[row.id];

                  const maxQty =
                    row.pending_qty ||
                    row.approved_qty ||
                    row.required_qty;

                  return (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRow(row)}
                        />
                      </td>

                      <td>{row.indent_no}</td>

                      <td>{row.material_name}</td>

                      <td>{row.approved_qty}</td>

                      <td>{row.pending_qty}</td>

                      <td>
                        {checked && (
                          <QuantityInput
                            value={selected[row.id].qty}
                            max={maxQty}
                            onChange={(val) =>
                              updateQty(row.id, val)
                            }
                          />
                        )}
                      </td>

                      <td>{row.uom}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* FOOTER */}
      <div className="pr-drawer-footer">

        <button
          className="btn-secondary"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          className="btn-primary"
          onClick={handleConfirm}
          disabled={!Object.keys(selected).length}
        >
          Consolidate Selected
        </button>

      </div>

    </div>
  );
}
